import * as React from 'react'

import { usePostComposer } from '~publish/hooks/usePostComposer'

import { useEngagementCoordinationStoreApi } from '../../context/EngagementCoordinationContext'
import { isCandidate } from './engagementCoordinationStore'
import type { CoordinationItem } from './engagementCoordinationStore'

type UseEngagementListCoordinationParams = {
  /**
   * Visible cards in render order with their intrinsic repliability. The list
   * computes `isRepliable` per feature (comments: status-based; mentions:
   * status + channel repliability); the store subtracts this session's
   * resolutions to derive the live candidacy.
   */
  items: ReadonlyArray<CoordinationItem>
  /**
   * Whether auto-expansion is active: the Unanswered filter
   * (`status === 'unreplied' | 'new'`) and the All filter, which pairs it with
   * `autoExpandTopOnly`. On every other filter the user chooses what to open.
   */
  shouldAutoExpand: boolean
  /**
   * Restricts auto-expansion to the top card: open it only when it's a
   * candidate, and never skip down to a later one. Used by the All filter,
   * where opening a card further down the list would be unexpected.
   */
  autoExpandTopOnly?: boolean
  /**
   * Whether the list's query is still fetching. Top-only mode waits for it to
   * settle before deciding: a cache-first emission can hold stale statuses
   * (e.g. a comment replied to this session still reads as unanswered), and
   * the decision is made only once per reset.
   */
  isLoading?: boolean
  /**
   * Collapses expansion and re-arms auto-expand when it changes. Built from the
   * filter values that reorder/refilter the list (status, sort, dates, post)
   * plus the channel selection.
   */
  resetExpansionKey: string
  /**
   * Clears the session resolution sets when it changes. Built from status,
   * post, and channel only — deliberately *not* sort/date, so reordering keeps
   * a card the user resolved greyed-out.
   */
  resetResolutionKey: string
  /** Card wrapper selector for outside-click detection. */
  cardWrapperSelector: string
}

/**
 * Drives the shared cross-card coordination store from a list's React tree:
 * syncs the navigation order, auto-expands the first repliable card (or, in
 * top-only mode, the top card if it's repliable), follows a newer card
 * arriving at the top, resets on filter/channel changes (two scopes), and
 * collapses on an outside click.
 *
 * All store reads here are imperative (`store.getState()`) so this hook — and
 * therefore the list — never subscribes to `expandedId`; only the per-card
 * wrappers subscribe, keeping navigation re-renders scoped to the affected
 * cards.
 */
export const useEngagementListCoordination = ({
  items,
  shouldAutoExpand,
  autoExpandTopOnly = false,
  isLoading = false,
  resetExpansionKey,
  resetResolutionKey,
  cardWrapperSelector,
}: UseEngagementListCoordinationParams): void => {
  const store = useEngagementCoordinationStoreApi()

  // Once auto-expansion has run for the current filter we don't re-run it, even
  // if nothing was expandable (a list of only notify-only cards shouldn't
  // re-scan every render). State, so re-arming on reset re-runs the effect.
  const [hasAutoExpanded, setHasAutoExpanded] = React.useState(false)
  const previousFirstId = React.useRef<string | null>(null)

  // A stable signature of the order so the sync effect fires on real changes,
  // not on every parent re-render (which hands a fresh `items` array).
  const itemsSignature = React.useMemo(
    () =>
      items
        .map((item) => `${item.id}:${item.isRepliable ? '1' : '0'}`)
        .join(','),
    [items],
  )

  // Keep the store's order in sync. setOrder also prunes a stale expandedId.
  React.useEffect(() => {
    store.getState().setOrder(items)
    // itemsSignature captures the meaningful shape of `items`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store, itemsSignature])

  // Reset everything tied to expansion when the filter/channel changes, and
  // re-arm auto-expansion.
  React.useEffect(() => {
    store.getState().resetExpansion()
    previousFirstId.current = null
    setHasAutoExpanded(false)
  }, [store, resetExpansionKey])

  // Clear the session resolution sets on the narrower trigger (status, post,
  // channel — not sort/date).
  React.useEffect(() => {
    store.getState().resetResolution()
  }, [store, resetResolutionKey])

  // Open the first candidate card once when the list loads (or, in top-only
  // mode, the top card if it's a candidate).
  // Candidacy is read against the live resolution set so a re-arm (e.g. after a
  // sort change, which resets expansion but not resolution) skips cards already
  // resolved this session. In the default mode we only arm `hasAutoExpanded`
  // once we actually expand — a render where the list is present but has no
  // candidate yet (e.g. the channel map isn't ready) must not permanently
  // disarm auto-expansion.
  React.useEffect(() => {
    if (!shouldAutoExpand || hasAutoExpanded || items.length === 0) return
    const { locallyResolved } = store.getState()
    // Top-only mode decides once, from the settled top card: a top card that
    // isn't a candidate disarms auto-expansion so a card further down never
    // pops open. A card the user opened while the list was still loading wins
    // over the top card.
    if (autoExpandTopOnly) {
      if (isLoading) return
      const { expandedId } = store.getState()
      if (expandedId === null && isCandidate(items[0], locallyResolved)) {
        store.getState().expand(items[0].id)
      }
      setHasAutoExpanded(true)
      return
    }
    const first = items.find((item) => isCandidate(item, locallyResolved))
    if (!first) return
    store.getState().expand(first.id)
    setHasAutoExpanded(true)

    // itemsSignature stands in for `items`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    store,
    shouldAutoExpand,
    autoExpandTopOnly,
    isLoading,
    hasAutoExpanded,
    itemsSignature,
  ])

  // When a newer card arrives at the top while the user is still on the
  // previous top card, move expansion up to the new arrival (if it's a
  // candidate).
  React.useEffect(() => {
    if (!shouldAutoExpand || items.length === 0) {
      return
    }
    const currentFirst = items[0]
    const { expandedId, locallyResolved } = store.getState()
    const arrivedNewFirst =
      previousFirstId.current !== null &&
      previousFirstId.current !== currentFirst.id &&
      expandedId === previousFirstId.current
    if (arrivedNewFirst && isCandidate(currentFirst, locallyResolved)) {
      store.getState().expand(currentFirst.id)
    }
    previousFirstId.current = currentFirst.id
    // itemsSignature stands in for `items`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store, shouldAutoExpand, itemsSignature])

  // Mirror the composer-open flag into a ref so the outside-click listener
  // doesn't re-subscribe on every composer toggle.
  const { isOpen: isComposerOpen } = usePostComposer()
  const isComposerOpenRef = React.useRef(isComposerOpen)
  React.useEffect(() => {
    isComposerOpenRef.current = isComposerOpen
  }, [isComposerOpen])

  // Collapse the expanded card when the user clicks outside the cards. The
  // listener is always attached and no-ops when nothing is expanded, so it
  // never reads `expandedId` reactively. While the post composer is open the
  // card stays expanded for any click — covering a backdrop click that the
  // shared `[role="dialog"]` check below doesn't catch, so opening "Start a
  // Post" from a reply form and dismissing it returns to the same open card.
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (isComposerOpenRef.current) return

      const target = event.target
      if (!(target instanceof Element)) return

      if (target.closest(cardWrapperSelector)) return
      if (target.closest('[role="dialog"]')) return
      if (target.closest('[data-radix-popper-content-wrapper]')) return
      if (target.closest('[role="tablist"]')) return

      if (store.getState().expandedId === null) return
      store.getState().collapse()
    }

    document.addEventListener('mousedown', handleClickOutside)
    return (): void => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [store, cardWrapperSelector])
}
